import { LitElement, html, css } from 'lit';
import { base, buttons, cards, forms } from '../styles.js';

/**
 * The eFolder documents to send with the order. They come from the HOST ADAPTER, not from the
 * origin response: the registered origin dataEntitlements are borrowers, property, terms and
 * closingDate, with no document entitlement, so the origin cannot carry them (spec Q-G).
 *
 * The selection is carried into F1 as request.resources. In F0 it is collected and nothing more.
 * The one rule -- at least one document, WHEN any are offered -- is enforced by `valid`, and only
 * spoken once the officer has emptied the list themselves: a form that scolds before it is touched
 * is noise. An EMPTY offer is valid (2026-09-30): the rule guards against ignoring offered
 * documents, and until ICE's document source is live every sandbox loan offers none, so requiring
 * a selection made Place order unreachable for a reason the officer cannot fix.
 */
export class DocumentPicker extends LitElement {
  static properties = {
    documents: { type: Array },
    selected: { type: Array },
    attachBusy: { type: Boolean },
    touched: { state: true },
  };

  static styles = [base, buttons, cards, forms, css`
    :host { display: block; }
    ul { list-style: none; padding: 0; margin: 0; }
    .empty { margin: 0; color: var(--pbg-gray-700); font-size: 13px; }
  `];

  constructor() {
    super();
    this.documents = [];
    this.selected = [];
    this.touched = false;
  }

  get valid() {
    return this.documents.length === 0 || this.selected.length > 0;
  }

  /** A submit attempt: speak the rule now even if the officer has not touched the list. */
  reportValidity() {
    this.touched = true;
    return this.valid;
  }

  render() {
    const showError = this.touched && !this.valid;
    return html`
      <div class="card">
        <div class="card-head">
          <span class="ch-title">Documents</span>
          <span class="ch-spacer"></span>
          ${this.documents.length
            ? html`<span class="ro-tag count" data-count aria-live="polite">${this.selected.length} selected</span>`
            : ''}
        </div>
        <div class="card-body">
          ${this.documents.length ? this.#list() : html`
            <p class="empty" data-empty>No documents attached yet. Attach from the eFolder to send documents with this order.</p>`}
          <p class="err-msg ${showError ? 'show' : ''}" data-error role="alert">
            ${showError ? 'Select at least one document to prepare.' : ''}
          </p>
          <div class="actions">
            <button type="button" class="btn btn-secondary btn-sm" data-action="attachDocuments"
                    ?disabled=${this.attachBusy} @click=${this.#attach}>
              ${this.attachBusy ? html`<span class="spin" aria-hidden="true"></span>Opening eFolder…` : 'Attach from eFolder'}
            </button>
          </div>
        </div>
      </div>`;
  }

  /** Asks the app to open ICE's eFolder picker; what comes back is appended to `documents`. */
  #attach = () => {
    this.dispatchEvent(new CustomEvent('attach-documents', { bubbles: true, composed: true }));
  };

  #list() {
    return html`
      <ul class="doc-list" aria-label="eFolder documents">
        ${this.documents.map((doc) => {
          const on = this.selected.includes(doc.id);
          return html`
            <li>
              <label class="doc-item ${on ? 'on' : ''}">
                <input type="checkbox" data-doc=${doc.id} .checked=${on}
                       @change=${(e) => this.#toggle(doc.id, e.target.checked)} />
                <span class="doc-meta">
                  <span class="dname">${doc.name}</span>
                  <span class="dsub">${formatDate(doc.dateUtc) ? `Attached ${formatDate(doc.dateUtc)}` : 'Attached in Encompass'}</span>
                  ${doc.type ? html`<span class="efolder">eFolder · ${doc.type}</span>` : ''}
                </span>
              </label>
            </li>`;
        })}
      </ul>`;
  }

  #toggle(id, checked) {
    this.touched = true;
    this.selected = checked ? [...this.selected, id] : this.selected.filter((x) => x !== id);
    this.dispatchEvent(new CustomEvent('documents-changed', {
      detail: { selected: this.selected, valid: this.valid }, bubbles: true, composed: true,
    }));
  }
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

customElements.define('epc-document-picker', DocumentPicker);
